import PropTypes from 'prop-types';
import React, { ReactElement, ReactNode, useContext } from 'react';
import styled from 'styled-components';
import H2 from './Heading2';
import HeaderEntityContainer from './HeaderEntityContainer';
import HeaderEntityValue from './HeaderEntityValue';
import BetaTag from '../../components/BetaTag';
import { QuestionCircleRegular } from '@fluentui/react-icons';
import { makeStyles, Tooltip } from '@fluentui/react-components';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import {
  ICompanyUserNameFromSystemUsernameQueryResult,
  ICompanyUserNameFromSystemUsernameQueryVariables,
  Maybe,
} from '../../graphql/schema/p4types';
import CompanyUserNameFromSystemUsername from '../../graphql/queries/company/CompanyUserNameFromSystemUsername.graphql';
import { useQuery } from '@apollo/client';
import { format, parseISO } from 'date-fns';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import type { Slot } from '@fluentui/react-utilities';

const Container = styled.div`
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

type IHeaderTitleProps = {
  value: ReactNode;
  subtitle?: string;
  optValue?: ReactNode;
  optValue2?: Maybe<string>;
  entityHdrStyle?: 'small';
  status?: ReactElement | ReactNode;
  beta?: boolean;
  hint?: Maybe<string>;
};

const useHintStyles = makeStyles({
  hint: {
    marginRight: '9px',
  },
});

const HeaderHint = ({ hint }: { hint: NonNullable<Slot<'div'>> }) => {
  const hintStyles = useHintStyles();
  if (!hint) {
    return null;
  }

  return (
    <Tooltip content={hint} relationship="label">
      <QuestionCircleRegular className={hintStyles.hint} />
    </Tooltip>
  );
};

const useSubtitleStyles = makeStyles({
  subtitle: {
    color: p4Tokens.colorNeutralForeground3,
  },
});

const useHeaderSubtitle = (subtitle?: string) => {
  const connectedForm = useContext(ConnectedFormContext);
  const { data } = useQuery<
    ICompanyUserNameFromSystemUsernameQueryResult,
    ICompanyUserNameFromSystemUsernameQueryVariables
  >(CompanyUserNameFromSystemUsername, {
    variables: {
      createdByUsername: connectedForm.values?.createdBy,
      updatedByUsername: connectedForm.values?.updatedBy,
    },
    skip: !(connectedForm.values?.id && connectedForm.values.createdBy),
  });
  const styles = useSubtitleStyles();

  if (data) {
    const createdAt = format(parseISO(connectedForm.values!.createdAt), 'do MMM yyyy, HH:mm');
    const updatedAt = format(parseISO(connectedForm.values!.updatedAt), 'do MMM yyyy, HH:mm');
    // In the highly unlikely/might be impossible scenario where no name was found, fall back to systemUserName (createdBy field).
    const createdBy: string = data.createdByName ?? connectedForm.values!.createdBy ?? 'Unknown';
    const updatedBy: string = data.updatedByName ?? connectedForm.values!.updatedBy ?? 'Unknown';
    const createdInfo = `Created by ${createdBy} on ${createdAt}`;
    const updatedInfo = `Updated by ${updatedBy} on ${updatedAt}`;

    const subtitleContent = [subtitle, createdInfo].filter((value) => !!value).join(', ');
    return (
      <Tooltip
        relationship={'description'}
        content={
          <>
            {createdInfo}
            <br />
            {updatedInfo}
          </>
        }
        positioning={'below'}
      >
        <div className={styles.subtitle}>
          <small>{subtitleContent}</small>
        </div>
      </Tooltip>
    );
  }

  return subtitle ? <small>{subtitle}</small> : null;
};

const HeaderTitle = ({
  value,
  optValue2,
  subtitle,
  optValue,
  entityHdrStyle,
  status,
  beta,
  hint,
}: IHeaderTitleProps) => {
  const subtitleWithTooltip = useHeaderSubtitle(subtitle);

  return (
    <Container>
      <H2>
        <HeaderEntityContainer>
          {value ? <HeaderEntityValue>{value}</HeaderEntityValue> : null}
          {optValue ? <HeaderEntityValue valueStyle={entityHdrStyle}>{optValue}</HeaderEntityValue> : null}
          {optValue2 ? <HeaderEntityValue valueStyle={entityHdrStyle}>{optValue2}</HeaderEntityValue> : null}
          {hint ? <HeaderHint hint={hint} /> : null}
          {status}
          {beta ? <BetaTag type="form" /> : null}
        </HeaderEntityContainer>
        {subtitleWithTooltip}
      </H2>
    </Container>
  );
};

HeaderTitle.propTypes = {
  optValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
};

HeaderTitle.defaultProps = {
  optValue: null,
  title: '',
  value: '',
};

export default HeaderTitle;
